<template>
  <div class="input-textarea relative">
    <div
      v-if="$slots['before-input']"
      class="text-on-surface dark:text-on-surface-dark absolute top-1 left-2"
      :class="$attrs.disabled ? 'cursor-not-allowed text-gray-400' : ''"
    >
      <slot name="before-input" />
    </div>
    <textarea
      v-bind="$attrs"
      v-model="innerModelValue"
      @input="onInput"
      @paste="onPaste"
      :disabled="!!$attrs.disabled"
      ref="textarea"
      draggable="false"
      @dragstart.prevent
      :placeholder="floatingPlaceholder ? ' ' : ($attrs.placeholder as string)"
      @focusin="hasFocus = true"
      @focusout="hasFocus = false"
      class="surface peer focus:border-secondary focus:ring-secondary disabled:dark:bg-surface/5! peer block max-h-full w-full resize-none rounded-md border-none p-1 leading-[normal]! select-none focus:ring-2 disabled:cursor-not-allowed! disabled:bg-gray-50! disabled:text-gray-400! sm:py-1.5 sm:text-sm"
      :class="[
        $slots['before-input'] ? 'pl-8' : '',
        errors?.length > 0 ? 'text-error ring-error placeholder:text-error focus:ring-error' : '',
        floatingPlaceholder ? 'pt-4!' : ''
      ]"
      :style="{ maxHeight: autosize ? '75dvh' : 'auto' }"
    />

    <span
      v-if="floatingPlaceholder && $attrs.placeholder"
      :class="$slots['before-input'] ? 'left-6' : ''"
      class="user-select-none peer-focus:text-on-suface peer-focus:tex-on-surface pointer-events-none absolute -inset-s-1 top-4 origin-left -translate-y-4 scale-75 transform px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:top-4 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2"
    >
      {{ $attrs.placeholder }}
    </span>
    <!-- Error message -->
    <p v-if="errors.length > 0" class="text-error mt-2 text-sm">
      {{ errors[0].$message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch, watchEffect } from "vue"
import type { ErrorObject } from "@vuelidate/core"
import useDelayedFunction from "@/composables/input/delayed-function.ts"

const emit = defineEmits<{
  input: [event: Event]
}>()

const props = defineProps({
  autosize: {
    type: Boolean,
    default: true
  },
  floatingPlaceholder: {
    type: Boolean,
    default: false
  },
  autoFocus: {
    type: Boolean,
    default: false
  },
  transformClipboard: {
    type: Function,
    default: undefined
  },
  errors: {
    type: Array as () => ErrorObject[],
    default: () => []
  }
})

defineOptions({
  inheritAttrs: false
})

const textarea = ref<HTMLTextAreaElement>()
const hasFocus = ref(false)

const modelValue = defineModel<string | null | undefined>({ default: undefined })
const innerModelValue = ref(modelValue.value)

watchEffect(() => {
  if (!hasFocus.value) {
    // When modelValue changes by some external source, update innerModelValue only if the textarea is not focused.
    innerModelValue.value = modelValue.value
  }
})

function onInput(event: Event) {
  modelValue.value = innerModelValue.value
  emit("input", event)
}

function onPaste(event: ClipboardEvent) {
  // Leave native paste behavior untouched unless prop is set
  if (!props.transformClipboard) {
    return
  }

  event.preventDefault()

  const pastedText = props.transformClipboard(event.clipboardData?.getData("text") ?? "")
  const element = event.target as HTMLTextAreaElement | null

  if (!element) {
    innerModelValue.value = pastedText
    modelValue.value = pastedText
    return
  }

  const currentValue = innerModelValue.value ?? ""
  const selectionStart = element.selectionStart ?? currentValue.length
  const selectionEnd = element.selectionEnd ?? currentValue.length

  // Mirror native textarea paste behavior by replacing only the active selection.
  const nextValue =
    currentValue.slice(0, selectionStart) + pastedText + currentValue.slice(selectionEnd)

  innerModelValue.value = nextValue
  modelValue.value = nextValue

  nextTick(() => {
    if (!textarea.value) {
      return
    }

    // Restore the caret just after the inserted text so continued typing feels natural.
    const cursorPosition = selectionStart + pastedText.length
    textarea.value.selectionStart = cursorPosition
    textarea.value.selectionEnd = cursorPosition
  })
}

function focus() {
  if (textarea.value) {
    textarea.value.focus()
  }
}

function blur() {
  if (textarea.value) {
    textarea.value.blur()
  }
}

// Debounce the height update to improve performance.
const delayedUpdateHeight = useDelayedFunction(updateHeight, 100)
let lastObservedWidth = 0

/**
 * Updates the height of the textarea to fit the content.
 */
function updateHeight() {
  if (!textarea.value) return

  textarea.value.style.height = "auto"

  // Include borders because scrollHeight excludes them from the border-box height.
  const borderHeight = textarea.value.offsetHeight - textarea.value.clientHeight
  textarea.value.style.height = `${textarea.value.scrollHeight + borderHeight}px`
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (props.autosize && textarea.value) {
    delayedUpdateHeight.schedule()

    // ResizeObserver is unavailable in the jsdom test environment.
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0

        if (width !== lastObservedWidth) {
          lastObservedWidth = width
          delayedUpdateHeight.schedule()
        }
      })

      resizeObserver.observe(textarea.value)
    }
  }

  if (props.autoFocus) {
    focus()
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

watch(modelValue, () => {
  if (props.autosize) {
    nextTick(() => {
      delayedUpdateHeight.schedule()
    })
  }
})

defineExpose({
  focus,
  blur,
  updateSize: updateHeight
})
</script>

<style scoped></style>
